import React, { useState, useRef, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * PasswordInput — front-end-only Show/Hide password toggle.
 *
 * Drop-in replacement for `<input type="password" />`. Renders the same
 * field with an Eye / EyeOff icon button positioned inside the right edge.
 *
 * Security notes (per UX-AUTH-1 spec):
 *   - Visibility state is local component state only (useState); never
 *     persisted, never logged, never sent anywhere.
 *   - The toggle button is `type="button"` so it cannot submit the form.
 *   - aria-label / aria-pressed attributes expose state to assistive tech.
 *   - Browser password managers see the same `name`/`autoComplete` props as
 *     they would on a raw <input type="password"> — we only flip the type
 *     attribute on the user's explicit click.
 *
 * Accepts every standard input prop (value, onChange, autoComplete, name,
 * required, minLength, placeholder, disabled, …) and a `data-testid`.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = "", inputClassName = "", testId, toggleTestId, ...inputProps },
  ref
) {
  const [visible, setVisible] = useState(false);
  const localRef = useRef(null);
  const setRefs = (el) => {
    localRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  const toggle = () => {
    setVisible((v) => !v);
    // Keep focus on the field so keyboard users don't lose context.
    if (localRef.current) {
      const el = localRef.current;
      // Defer focus to next tick so the type-flip has applied.
      setTimeout(() => el.focus({ preventScroll: true }), 0);
    }
  };

  const Icon = visible ? EyeOff : Eye;
  const label = visible ? "Hide password" : "Show password";

  return (
    <div className={`relative ${className}`}>
      <input
        ref={setRefs}
        type={visible ? "text" : "password"}
        {...inputProps}
        data-testid={testId}
        className={`w-full bg-[var(--rs-bg-2)] border border-[var(--rs-border)] rounded-md pl-3 pr-10 py-2 text-sm ${inputClassName}`}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        aria-pressed={visible}
        title={label}
        data-testid={toggleTestId || (testId ? `${testId}-toggle` : undefined)}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md inline-flex items-center justify-center text-[var(--rs-text-3,#6b7785)] hover:text-[var(--rs-amber,#f5b400)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rs-amber,#f5b400)]"
      >
        <Icon className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
});

export default PasswordInput;
